import { Text as TextNode } from 'slate'
import {
  GetProps,
  SizableText,
  Stack,
  XStack,
  YStack,
  YStackProps,
} from 'tamagui'
import { TextLink } from './TextLink'
import { interaction } from '@mythical/ui'

type TextProps = GetProps<typeof SizableText>

interface Node {
  type: string
  children: Node[]
  [key: string]: any
}

interface Serialize {
  children: Node[]
  color: string
  isOrdered?: boolean
  textProps?: TextProps
}

const serialize = ({ children, color, isOrdered, textProps }: Serialize) => {
  const Text = (props: TextProps) => (
    <SizableText size="$4" {...textProps} {...props} />
  )

  if (!children) {
    return null
  }

  return children.map((node, i) => {
    if (TextNode.isText(node)) {
      const nodeProps = {
        textDecorationColor:
          node.textColor || textProps?.color || color || '$fgPrimary',
        ...textProps,
        ...(node.bold && { fow: '600', $gtSm: { fow: '600' } }),
        ...(node.italic && { fontStyle: 'italic' }),
        ...(node.underline && { textDecorationLine: 'underline' }),
        ...(node.strikethrough && { textDecorationLine: 'line-through' }),
        ...(node.textColor && { color: node.textColor, o: 1 }),
      }
      return (
        <Text key={i} {...nodeProps}>
          {node.text}
        </Text>
      )
    }

    if (!node) {
      return null
    }

    switch (node.type) {
      case 'h1':
        return serialize({
          children: node.children,
          color,
          textProps: { ...textProps, tag: 'h2', size: '$8', fow: '700', $gtSm: { size: '$9', fow: '700' } },
        })
      case 'h2':
        return serialize({
          children: node.children,
          color,
          textProps: { ...textProps, tag: 'h3', size: '$7', fow: '700', $gtSm: { size: '$8', fow: '700' } },
        })
      case 'h3':
        return serialize({
          children: node.children,
          color,
          textProps: { ...textProps, tag: 'h4', size: '$6', fow: '600', $gtSm: { size: '$7', fow: '600' } },
        })
      case 'indent':
        return (
          <Stack
            borderLeftWidth={2}
            borderLeftColor="$fgPrimary"
            pl="$3.5"
            key={i}
          >
            {serialize({ children: node.children, color })}
          </Stack>
        )
      case 'center': {
        return (
          <Stack alignItems="center" justifyContent="center" key={i}>
            {serialize({
              children: node.children,
              color,
              textProps: {
                textAlign: 'center',
              },
            })}
          </Stack>
        )
      }
      case 'ul':
        return (
          <YStack gap="$2.5" key={i} tag="ul">
            {serialize({ children: node.children, color })}
          </YStack>
        )
      case 'ol':
        return (
          <YStack gap="$2.5" key={i} tag="ol">
            {serialize({ children: node.children, color, isOrdered: true })}
          </YStack>
        )
      case 'li':
        return (
          <XStack tag="li" fs={1} key={i} gap="$2.5" pl="$2.5">
            <Text fs={1}>{isOrdered ? `${i + 1}. ` : '•'}</Text>
            <Text fs={1} tag="span" o={1}>
              {serialize({
                children: node.children,
                color,
                isOrdered,
                textProps: { fs: 1 },
              })}
            </Text>
          </XStack>
        )
      case 'link':
        const url = node.url

        // Prevent creating broken links
        if (!url) {
          return null
        }

        return (
          <TextLink
            href={url}
            key={i}
            size="$6"
            textDecorationLine="none"
            {...(node.newTab && { target: '_blank' })}
          >
            {serialize({
              children: node.children,
              color,
              textProps: {
                color: '$brandPink',
                o: 1,
                textDecorationLine: 'underline',
                ...interaction.opacity,
              },
            })}
          </TextLink>
        )

      default:
        return (
          <Text key={i} tag="p" o={1}>
            {serialize({ children: node.children, color, textProps })}
          </Text>
        )
    }
  })
}

export interface RichTextProps {
  content: any
  color?: string
  textProps?: TextProps
}

export function RichText({
  content,
  color = '$fgPrimary',
  textProps,
  ...wrapperProps
}: RichTextProps & Omit<YStackProps, 'content'>) {
  return (
    <YStack gap="$2.5" $gtSm={{ gap: '$3.5' }} {...wrapperProps}>
      {serialize({ children: content, color, textProps })}
    </YStack>
  )
}

export default RichText
